import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { obtenerPrograma, construirHojas, calcularProyeccionFin } from '../../servicios/programaService';
import TablaAvancePivot from './TablaAvancePivot';

function colorSec(pct) {
  if (pct === 100) return { bar: '#16a34a', text: '#15803d' };
  if (pct >= 70)   return { bar: '#2563eb', text: '#1d4ed8' };
  if (pct >= 30)   return { bar: '#ea580c', text: '#c2410c' };
  return             { bar: '#dc2626', text: '#b91c1c' };
}

function calcularDiasRetrasoSeccion(hojas, hoy) {
  return calcularProyeccionFin(hojas, hoy).diasRetraso;
}

export default function ProgresoPrograma() {
  const [datos, setDatos] = useState({ programa: null, secciones: [], actividades: [] });
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try { setDatos(await obtenerPrograma()); }
    catch { /* silencioso */ }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const hojas = useMemo(() => construirHojas(datos.actividades), [datos.actividades]);

  const pctGlobal = useMemo(() => {
    let sumDur = 0, sumAv = 0;
    for (const h of hojas) {
      const dur = h.duracionBase || 1;
      sumDur += dur; sumAv += (h.porcentajeAvance || 0) * dur;
    }
    return sumDur > 0 ? Math.round((sumAv / sumDur) * 10) / 10 : 0;
  }, [hojas]);

  const totalCompletadas = useMemo(
    () => hojas.filter(h => (h.porcentajeAvance ?? 0) === 100).length,
    [hojas]
  );

  // Agrupar hojas por nivel-1 del WBS → stats por sección
  const seccionStats = useMemo(() => {
    const fechaSantiago = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santiago' });
    const hoy = new Date(fechaSantiago + 'T00:00:00-04:00');

    const secMap = {};
    for (const s of datos.secciones) {
      if (s.wbsCode) secMap[s.wbsCode] = { nombre: s.nombre, orden: s.orden ?? 9999 };
    }
    const grupos = {};
    for (const h of hojas) {
      const nivel1 = h.wbsCode ? h.wbsCode.split('.')[0] : '__sin__';
      if (!grupos[nivel1]) grupos[nivel1] = { hojas: [], nombre: secMap[nivel1]?.nombre || nivel1, orden: secMap[nivel1]?.orden ?? 9999 };
      grupos[nivel1].hojas.push(h);
    }
    return Object.entries(grupos)
      .sort((a, b) => a[1].orden - b[1].orden)
      .map(([nivel1, { hojas: hs, nombre, orden }]) => {
        let sumDur = 0, sumAv = 0;
        for (const h of hs) {
          const dur = h.duracionBase || 1;
          sumDur += dur; sumAv += (h.porcentajeAvance || 0) * dur;
        }
        const pct = sumDur > 0 ? Math.round(sumAv / sumDur) : 0;
        const diasRetraso = calcularDiasRetrasoSeccion(hs, hoy);
        return {
          nivel1, nombre, orden,
          pct,
          total: hs.length,
          completadas: hs.filter(h => (h.porcentajeAvance ?? 0) === 100).length,
          diasRetraso,
        };
      });
  }, [datos.secciones, hojas]);

  const seccionesCompletas = seccionStats.filter(s => s.pct === 100).length;

  const avancePorSeccionTabla = useMemo(() => {
    if (!seccionStats.length) return null;
    return (
      <div style={{ overflowX: 'auto' }}>
        <table className="programa-tabla" style={{ minWidth: 420 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', paddingLeft: 12, fontSize: '0.72rem', color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>SECCIÓN</th>
              <th style={{ textAlign: 'center', minWidth: 80, fontSize: '0.72rem', color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>AVANCE</th>
              <th style={{ textAlign: 'center', minWidth: 90, fontSize: '0.72rem', color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>DÍAS ATRASO</th>
              <th style={{ textAlign: 'right', paddingRight: 14, fontSize: '0.72rem', color: '#64748b', fontWeight: 600, background: '#f8fafc', minWidth: 120 }}>BARRA</th>
            </tr>
          </thead>
          <tbody>
            {seccionStats.map(sec => {
              const c = colorSec(sec.pct);
              const dr = sec.diasRetraso;
              const drColor = dr === null ? '#94a3b8' : dr > 0 ? '#dc2626' : '#16a34a';
              const drLabel = dr === null ? '—' : dr > 0 ? `+${dr}d` : `${dr}d`;
              return (
                <tr key={sec.nivel1} className="programa-fila-actividad">
                  <td style={{ paddingLeft: 12, fontSize: '0.82rem', color: '#334155', fontWeight: 600 }}>{sec.nombre}</td>
                  <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                    <span style={{ fontSize: '0.88rem', fontWeight: 800, color: c.text }}>{sec.pct}%</span>
                  </td>
                  <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 800, color: drColor }}>{drLabel}</span>
                  </td>
                  <td style={{ padding: '6px 14px 6px 8px' }}>
                    <div style={{ flex: 1, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${sec.pct}%`, background: c.bar, borderRadius: 3, transition: 'width 0.5s ease' }} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }, [seccionStats]);

  if (cargando) return (
    <div className="programa-container">
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 20px', marginBottom: 20 }}>
        <div className="programa-skeleton-block" style={{ width: '40%', height: 30, marginBottom: 10 }} />
        <div className="programa-skeleton-block" style={{ width: '25%', height: 13, marginBottom: 14 }} />
        <div className="programa-skeleton-block" style={{ width: '100%', height: 7 }} />
      </div>
      {[1,2,3,4,5].map(i => (
        <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
          <div className="programa-skeleton-block" style={{ width: 130, height: 13 }} />
          <div className="programa-skeleton-block" style={{ flex: 1, height: 6 }} />
          <div className="programa-skeleton-block" style={{ width: 38, height: 13 }} />
        </div>
      ))}
    </div>
  );
  if (!datos.programa) return <div className="programa-container"><div className="programa-empty"><h3>Sin programa importado</h3></div></div>;

  return (
    <div className="programa-container">
      {/* Banner resumen */}
      <div style={{
        background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px',
        padding: '16px 20px', marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#111827' }}>
              {totalCompletadas.toLocaleString()}
              <span style={{ fontSize: '0.9rem', fontWeight: 400, color: '#64748b', marginLeft: 6 }}>
                de {hojas.length.toLocaleString()} partidas completadas
              </span>
            </div>
            <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 3 }}>
              <span style={{ color: '#16a34a', fontWeight: 600 }}>✓ {seccionesCompletas} sección{seccionesCompletas !== 1 ? 'es' : ''} al 100%</span>
              {'  ·  '}
              <span style={{ color: '#ea580c' }}>{seccionStats.length - seccionesCompletas} en progreso</span>
            </div>
          </div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#16a34a' }}>{pctGlobal}%</div>
        </div>
        <div style={{ marginTop: '10px', height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 4, width: `${pctGlobal}%`,
            background: 'linear-gradient(90deg, #22c55e, #16a34a)', transition: 'width 0.6s ease'
          }} />
        </div>
      </div>

      <TablaAvancePivot
        actividades={datos.actividades}
        secciones={datos.secciones}
        avancePorSeccionTabla={avancePorSeccionTabla}
        mostrarTodos={true}
      />
    </div>
  );
}
